import * as React from "react";
import { cn } from "@/lib/utils";

interface InputProps extends React.InputHTMLAttributes<HTMLInputElement> {
   /**
    * "light" = grey-on-white style
    * "dark"  = white-on-dark style
    * default is "light"
    */
   variant?: "light" | "dark";
   error?: boolean;
}

export function Input({
   variant = "light",
   error,
   className,
   value,
   placeholder,
   ...props
}: InputProps) {
   const isFilled = typeof value === "string" && value.length > 0;

   // base: pill shape, padding, no outline, smooth border transitions
   const base =
      "w-full rounded-[60px] border px-6 py-3 text-fs-m-body-m-line transition-colors outline-none";

   // now choose colors based on variant + state
   const styles = {
      light: {
         text: "text-custom-dark placeholder:text-custom-dark/70",
         borderEmpty: "border-custom-dark/20",
         borderFilled: "border-custom-dark",
         borderError: "border-red-500",
         hoverBorder: "hover:border-custom-dark",
      },
      dark: {
         text: "text-white placeholder:text-white/70 bg-custom-dark",
         borderEmpty: "border-white/20",
         borderFilled: "border-white/60",
         borderError: "border-red-500",
         hoverBorder: "hover:border-white/60",
      },
   }[variant];

   // pick the right border color
   const borderColor = error
      ? styles.borderError
      : isFilled
        ? styles.borderFilled
        : styles.borderEmpty;

   // only allow hover/focus darkening if not error
   const interactive =
      !error && `${styles.hoverBorder} focus:${styles.hoverBorder}`;

   return (
      <input
         {...props}
         value={value}
         placeholder={placeholder}
         className={cn(base, styles.text, borderColor, interactive, className)}
      />
   );
}
